import type { TRPC_ERROR_CODE_KEY } from '@trpc/server/rpc'
import { errorCode } from '@/lib/trpc-error'

/**
 * Re-exported so the coaching callers keep one import for the whole family.
 * The reader itself is generic and lives in `lib`: the assertion bank needs it
 * too, and a second copy would have drifted from this one.
 */
export { errorCode }

/**
 * Codes whose message the coaching server writes deliberately, for an operator
 * to read. Anything outside this set carries whatever the thrown error
 * happened to say — a Postgres string, a stack fragment — so it is never
 * quoted into the UI.
 *
 * CONFLICT is excluded on purpose. The server knows only that a version check
 * rejected the command; the caller knows *which* command, and a stale note and
 * a stale plan are different accidents that need different sentences. Callers
 * keep naming their own conflict.
 *
 * BAD_REQUEST is excluded for a harder reason: it is not exclusively ours.
 * tRPC raises BAD_REQUEST for input validation, and that message is the
 * serialized zod issue array, produced by the input parser before any resolver
 * — and so before the coaching error boundary can replace it. Quoting it would
 * paste `[{"code":"unrecognized_keys",...}]` into the board flash, which is
 * exactly the leak this set exists to prevent. Every mutating coaching input
 * is `.strict()`, so this is a live path, not a hypothetical one.
 */
const EXPLAINED_CODES: ReadonlySet<TRPC_ERROR_CODE_KEY> =
  new Set<TRPC_ERROR_CODE_KEY>([
    'NOT_FOUND',
    'FORBIDDEN',
    'UNAUTHORIZED',
    'PRECONDITION_FAILED',
  ])

/**
 * Codes where replaying the identical command could plausibly land
 * differently. Everything else — a rejected precondition, a bad input, a
 * missing record — returns the same error forever, so offering a retry is a
 * lie (LG-272).
 *
 * A CONFLICT is deliberately absent. It wrote nothing, but the state the
 * command was built from moved underneath it; re-issuing is the operator's
 * call after reading the refreshed plan, not a button the dashboard offers.
 *
 * The gateway codes are here because they are transport weather: the request
 * may never have reached a procedure, and the next one may well succeed.
 */
const RETRYABLE_CODES: ReadonlySet<TRPC_ERROR_CODE_KEY> =
  new Set<TRPC_ERROR_CODE_KEY>([
    'INTERNAL_SERVER_ERROR',
    'TIMEOUT',
    'SERVICE_UNAVAILABLE',
    'BAD_GATEWAY',
    'GATEWAY_TIMEOUT',
  ])

/**
 * The server's own explanation of a failure, or null when it did not give one
 * that is safe to show. Callers pair this with their own copy: the reason says
 * *why the server refused*, the caller says *which command it was* and whether
 * the follow-up refresh worked.
 */
export function errorReason(error: unknown): string | null {
  const code = errorCode(error)
  if (code === null || !EXPLAINED_CODES.has(code)) return null
  if (!error || typeof error !== 'object') return null
  const candidate = error as {
    message?: unknown
    shape?: { message?: unknown }
  }
  const message = candidate.message ?? candidate.shape?.message
  if (typeof message !== 'string') return null
  const trimmed = message.trim()
  return trimmed.length > 0 ? trimmed : null
}

/**
 * Whether to offer the operator a retry. A failure with no tRPC code at all
 * never reached a procedure — a dropped connection or an aborted fetch — which
 * is the case a retry exists for.
 */
export function isRetryableFailure(error: unknown): boolean {
  const code = errorCode(error)
  return code === null || RETRYABLE_CODES.has(code)
}
